import type { ApplicationDraft } from "../../api/client";
import { SelectField } from "../../components/SelectField";
import { TextAreaField } from "../../components/TextAreaField";
import { TextField } from "../../components/TextField";

/**
 * Screen 01 · Legal identity.
 *
 * The legal entity that holds the accreditation and issues the certificates. This name
 * appears on every audit report Complao generates, so it is the registered name and not
 * the trading one — both are held, and they are different fields for that reason.
 *
 * Every field here except the trading name is `not null` in data dictionary §2. The
 * prototype accepted them blank; §8 says the dictionary governs the build.
 */
export function LegalIdentityStep({
  draft,
  set,
  countries,
  errorFor,
}: {
  draft: ApplicationDraft;
  set: (patch: Partial<ApplicationDraft>) => void;
  countries: readonly string[];
  errorFor: (field: string) => string | undefined;
}) {
  return (
    <section className="card">
      <h2>Legal identity</h2>
      <p className="sub" style={{ marginBottom: "14px" }}>
        The legal entity that holds the accreditation and will issue certificates. This name
        appears on every audit report Complao generates.
      </p>

      <div className="grid2">
        <TextField
          id="legal_name"
          label="Legal entity name"
          value={draft.legal_name ?? ""}
          onChange={(value) => set({ legal_name: value })}
          hint="As registered"
          error={errorFor("legal_name")}
          required
        />
        <TextField
          id="trading_name"
          label="Trading name"
          value={draft.trading_name ?? ""}
          onChange={(value) => set({ trading_name: value })}
          hint="If different from the legal name"
          error={errorFor("trading_name")}
        />
      </div>

      <div className="grid2">
        <TextField
          id="registration_number"
          label="Company registration number"
          value={draft.registration_number ?? ""}
          onChange={(value) => set({ registration_number: value })}
          hint="Company or tax registration number"
          error={errorFor("registration_number")}
          required
        />
        <SelectField
          id="country"
          label="Country of registration"
          value={draft.country ?? ""}
          onChange={(value) => set({ country: value })}
          options={countries.map((name) => ({ value: name, label: name }))}
          error={errorFor("country")}
          required
        />
      </div>

      <TextAreaField
        id="address"
        label="Registered address"
        value={draft.address ?? ""}
        onChange={(value) => set({ address: value })}
        error={errorFor("address")}
        required
      />
    </section>
  );
}
